import { useEffect, useRef, useState } from "react";

/**
 * Subscribe to the Cathedral's live integrity WebSocket.
 * Returns: { data, status }
 *   status: "connecting" | "live" | "closed" | "error"
 */
export function useIntegrityStream({ intervalSeconds = 5 } = {}) {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("connecting");
  const wsRef = useRef(null);

  useEffect(() => {
    const httpBase = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    const wsBase = httpBase.replace(/^http/, "ws");
    const url = `${wsBase}/api/ws/integrity`;
    let closedByUs = false;
    let reconnectTimer = null;

    function connect() {
      setStatus("connecting");
      let ws;
      try {
        ws = new WebSocket(url);
      } catch {
        setStatus("error");
        return;
      }
      wsRef.current = ws;
      ws.onopen = () => {
        try { ws.send(JSON.stringify({ interval_seconds: intervalSeconds })); }
        catch (e) { console.warn("integrity-stream open send failed", e); }
        setStatus("live");
      };
      ws.onmessage = (ev) => {
        try { setData(JSON.parse(ev.data)); }
        catch (e) { console.warn("integrity-stream parse failed", e); }
      };
      ws.onerror = () => setStatus("error");
      ws.onclose = () => {
        setStatus("closed");
        if (!closedByUs) {
          reconnectTimer = setTimeout(connect, 4000);
        }
      };
    }

    connect();
    return () => {
      closedByUs = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      try { wsRef.current?.close(); }
      catch (e) { console.warn("integrity-stream close failed", e); }
    };
    // All other refs are scoped inside the effect; only intervalSeconds is a true dep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intervalSeconds]);

  return { data, status };
}
